import { useDeleteTeacher } from "./useDeleteTeacher.ts";
import { useConfirm } from "../UI/Feedback/useConfirm.ts";
import { DropDown } from "../UI/DropDown/DropDown.tsx";
import {
  CalendarIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  StarIcon,
} from "@heroicons/react/24/outline";
import { DropDownItem } from "../UI/DropDown/DropDownItem.tsx";
import { useToTeacherAvailability } from "./TeacherAvailability/useToTeacherAvailability.ts";
import {
  ArrowTopRightOnSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline/index.js";
import { PrimaryLoadingIndicator } from "../UI/Loading/PrimaryLoadingIndicator.tsx";
import { LoaderSize } from "../UI/Loading/LoaderSize.tsx";
import { ErrorMessagePopup } from "../UI/MessagePopup/ErrorMessagePopup.tsx";
import { useMessagePopupController } from "../UI/MessagePopup/useMessagePopupController.ts";
import { ApolloError } from "@apollo/client";
import { useToEditFranchisee } from "./useToEditFranchisee.ts";
import { useToTeacherDetail } from "./useToTeacherDetail.ts";

export function TeacherTableDotDropdown({
  teacherId,
  authId,
}: {
  teacherId: string;
  authId: string;
}) {
  const { toTeacherAvailability } = useToTeacherAvailability();
  const { toEditFranchisee } = useToEditFranchisee();
  const { toTeacherDetail } = useToTeacherDetail();
  const { loading: deleting, deleteTeacher } = useDeleteTeacher();
  const askConfirmation = useConfirm();
  const { show, showPopup, description, title } = useMessagePopupController(
    4 * 1000,
  );

  async function confirmDeleteTeacher() {
    await deleteTeacher(teacherId);
  }

  async function requestDeleteTeacher() {
    if (
      await askConfirmation({
        title: "Delete this teacher?",
        message: "This action cannot be undone.",
        confirmLabel: "Delete",
        isDestructive: true,
      })
    ) {
      try {
        await confirmDeleteTeacher();
      } catch (e) {
        const error = e as ApolloError;
        showPopup("An error occurred", error?.message ?? "Unknown error");
      }
    }
  }
  return (
    <>
      <ErrorMessagePopup title={title} description={description} show={show} />
      <div className={"flex justify-end"}>
        <div>
          <DropDown
            openInFloatingLayer
            activator={
              <EllipsisHorizontalIcon
                className={"text-slate-800 w-5 h-5 group-hover:text-white"}
              />
            }
          >
            <DropDownItem onClick={() => toTeacherAvailability(teacherId)}>
              <CalendarIcon className={"w-5 h-5 mr-2 flex-shrink-0"} />
              Availability
            </DropDownItem>
            <DropDownItem
              onClick={() => {
                window.open(
                  `${import.meta.env.VITE_TEACHER_AREA_APP_BASE_URL}/impersonate-user?uid=${authId}`,
                  "_blank",
                  "noopener noreferrer",
                );
              }}
            >
              <ArrowTopRightOnSquareIcon
                className={"w-5 h-5 mr-2 flex-shrink-0"}
              />
              Impersonate
            </DropDownItem>
            <DropDownItem onClick={() => toTeacherDetail(teacherId)}>
              <StarIcon className={"w-5 h-5 mr-2 flex-shrink-0"} />
              Overview
            </DropDownItem>
            <DropDownItem onClick={() => toEditFranchisee(teacherId)}>
              <PencilSquareIcon className={"w-5 h-5 mr-2 flex-shrink-0"} />
              Edit
            </DropDownItem>
            <DropDownItem onClick={requestDeleteTeacher}>
              {deleting ? (
                <span className={"mr-2"}>
                  <PrimaryLoadingIndicator size={LoaderSize.Small} />
                </span>
              ) : (
                <TrashIcon className={"w-5 h-5 mr-2 flex-shrink-0"} />
              )}
              Delete
            </DropDownItem>
          </DropDown>
        </div>
      </div>
    </>
  );
}
